import React, { useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Button,
    IconButton,
    Box,
    Typography,
    CircularProgress,
    Paper,
    Divider,
    Chip,
    Stack,
    Card,
    CardContent
} from "@mui/material";
import { Add, Remove, Close, Edit as EditIcon } from "@mui/icons-material";
import { format } from "date-fns";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import KeywordsInput from "../KeywordsInput";

const EditProductModal = ({
    open,
    onClose,
    input,
    onChange,
    onSubmit,
    categories,
    errors,
    specificationErrors,
    keywordsError,
    isDealerPriceEnabled,
    canSubmit,
    loading,
    onSpecificationChange,
    addSpecificationRow,
    removeSpecificationRow,
    touched
}) => {
    useEffect(() => {
        if (open) {
            console.log('EditProductModal Debug:', {
                canSubmit,
                specificationErrors,
                specifications: input.specification,
                hasSpecErrors: specificationErrors.some(specError => Object.values(specError).some(e => e)),
                input: input,
                discounted_price: input.discount_price,
                stock_quantity: input.stock_quantity
            });
        }
    }, [open, canSubmit, specificationErrors, input]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <EditIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Edit Product
                    </Typography>
                    {canSubmit && (
                        <Chip
                            label="Ready"
                            size="small"
                            sx={{
                                bgcolor: 'rgba(76, 175, 80, 0.9)',
                                color: 'white',
                                fontWeight: 600
                            }}
                        />
                    )}
                </Stack>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <MDBox component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <DialogContent
                    sx={{
                        bgcolor: '#f8f9fa',
                        p: 3,
                        overflowY: 'auto',
                        flex: 1
                    }}
                >
                    <Stack spacing={3}>
                        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#667eea' }}>
                                    Basic Information
                                </Typography>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Product Name"
                                            name="product_name"
                                            value={input.product_name || ""}
                                            onChange={onChange}
                                            fullWidth
                                            error={touched?.product_name && !!errors.product_name}
                                            helperText={touched?.product_name && errors.product_name}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover fieldset': { borderColor: '#667eea' },
                                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="SKU"
                                            name="sku"
                                            value={input.sku || ""}
                                            onChange={onChange}
                                            fullWidth
                                            error={touched?.sku && !!errors.sku}
                                            helperText={touched?.sku && errors.sku}
                                            disabled
                                            sx={{
                                                bgcolor: '#f5f5f5',
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#666'
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth error={touched?.category_name && !!errors.category_name}>
                                            <InputLabel>Product Category</InputLabel>
                                            <Select
                                                label="Product Category"
                                                name="category_name"
                                                value={input.category_name || ""}
                                                onChange={onChange}
                                                sx={{
                                                    height: '56px',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#c4c4c4'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' }
                                                }}
                                            >
                                                {categories.map((category) => (
                                                    <MenuItem key={category.id} value={category.category_name}>
                                                        {category.category_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {touched?.category_name && errors.category_name && (
                                                <FormHelperText>{errors.category_name}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Restock Date"
                                            type="date"
                                            name="restock_date"
                                            value={input.restock_date ? format(new Date(input.restock_date), "yyyy-MM-dd") : ""}
                                            onChange={(e) => onChange({ target: { name: "restock_date", value: e.target.value } })}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: format(new Date(), "yyyy-MM-dd") }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Description"
                                            name="description"
                                            value={input.description || ""}
                                            onChange={onChange}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            error={touched?.description && !!errors.description}
                                            helperText={touched?.description && errors.description}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover fieldset': { borderColor: '#667eea' },
                                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Warranty Description (Optional)"
                                            name="warranty_description"
                                            value={input.warranty_description || ""}
                                            onChange={onChange}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            placeholder="e.g., 6-month warranty on electronic components"
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#667eea' }}>
                                    Pricing & Stock
                                </Typography>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            label="Price"
                                            name="price"
                                            value={input.price || ""}
                                            onChange={onChange}
                                            type="number"
                                            fullWidth
                                            error={touched?.price && !!errors.price}
                                            helperText={touched?.price && errors.price}
                                            InputProps={{
                                                startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>₹</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            label="Discounted Price"
                                            name="discounted_price"
                                            value={input.discount_price || ""}
                                            onChange={onChange}
                                            type="number"
                                            fullWidth
                                            error={touched?.discounted_price && !!errors.discounted_price}
                                            helperText={touched?.discounted_price && errors.discounted_price}
                                            InputProps={{
                                                startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>₹</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            label="Discount (%)"
                                            name="discount"
                                            value={input.discount || ""}
                                            type="number"
                                            fullWidth
                                            disabled
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: <Typography sx={{ ml: 1, color: '#666' }}>%</Typography>
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    bgcolor: '#f5f5f5'
                                                },
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#666',
                                                    bgcolor: '#f5f5f5'
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            label="Stock Quantity"
                                            name="stock_quantity"
                                            value={input.stock_quantity || ""}
                                            onChange={onChange}
                                            type="number"
                                            fullWidth
                                            error={touched?.stock_quantity && !!errors.stock_quantity}
                                            helperText={touched?.stock_quantity && errors.stock_quantity}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth error={touched?.gst_type && !!errors.gst_type}>
                                            <InputLabel>GST Type</InputLabel>
                                            <Select
                                                label="GST Type"
                                                name="gst_type"
                                                value={input.gst_type || ""}
                                                onChange={onChange}
                                                sx={{
                                                    height: '56px',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#c4c4c4'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' }
                                                }}
                                            >
                                                <MenuItem value="Include GST">Include GST</MenuItem>
                                                <MenuItem value="Exclude GST">Exclude GST</MenuItem>
                                                <MenuItem value="No GST">No GST</MenuItem>
                                            </Select>
                                            {touched?.gst_type && errors.gst_type && (
                                                <FormHelperText>{errors.gst_type}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    {(input.gst_type === "Include GST" || input.gst_type === "Exclude GST") && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="GST Percentage (%)"
                                                name="gst_percentage"
                                                value={input.gst_percentage || ""}
                                                onChange={onChange}
                                                type="number"
                                                fullWidth
                                                error={touched?.gst_percentage && !!errors.gst_percentage}
                                                helperText={touched?.gst_percentage && errors.gst_percentage}
                                                InputProps={{
                                                    endAdornment: <Typography sx={{ ml: 1, color: '#666' }}>%</Typography>
                                                }}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
                                        Specifications
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Chip
                                            label={`${input.specification?.length || 0} items`}
                                            size="small"
                                            sx={{ bgcolor: '#667eea', color: 'white' }}
                                        />
                                        {specificationErrors.some(specError => Object.values(specError).some(e => e)) && (
                                            <Chip
                                                label="Has Errors"
                                                size="small"
                                                color="error"
                                            />
                                        )}
                                    </Stack>
                                </Box>
                                <Stack spacing={2}>
                                    {(input.specification ?? []).map((spec, index) => (
                                        <Paper
                                            key={index}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                bgcolor: '#f8f9fa',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1.5
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={5}>
                                                    <TextField
                                                        label="Title"
                                                        value={spec?.title || ""}
                                                        onChange={(e) => onSpecificationChange(index, "title", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        error={!!specificationErrors[index]?.title}
                                                        helperText={specificationErrors[index]?.title}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={5}>
                                                    <TextField
                                                        label="Description"
                                                        value={spec?.desc || ""}
                                                        onChange={(e) => onSpecificationChange(index, "desc", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        error={!!specificationErrors[index]?.desc}
                                                        helperText={specificationErrors[index]?.desc}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        <IconButton
                                                            onClick={() => removeSpecificationRow(index)}
                                                            disabled={input.specification.length === 1}
                                                            size="small"
                                                            sx={{
                                                                color: 'error.main',
                                                                '&:hover': { bgcolor: 'error.light' }
                                                            }}
                                                        >
                                                            <Remove />
                                                        </IconButton>
                                                        {index === input.specification.length - 1 && (
                                                            <IconButton
                                                                onClick={addSpecificationRow}
                                                                size="small"
                                                                sx={{
                                                                    color: 'success.main',
                                                                    '&:hover': { bgcolor: 'success.light' }
                                                                }}
                                                            >
                                                                <Add />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#667eea' }}>
                                    Keywords
                                </Typography>
                                <KeywordsInput
                                    label="Products Related Keywords"
                                    keywords={Array.isArray(input.keywords) ? input.keywords : (input.keywords || "").split(",").filter(Boolean)}
                                    onChange={(newKeywords) => onChange({ target: { name: "keywords", value: newKeywords } })}
                                />
                                {keywordsError && (
                                    <Typography variant="caption" color="error" sx={{ ml: 1, mt: 1, display: 'block' }}>
                                        {keywordsError}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>
                </DialogContent>
                <Divider sx={{ flexShrink: 0 }} />
                <DialogActions
                    sx={{
                        p: 3,
                        bgcolor: '#f8f9fa',
                        flexShrink: 0,
                        boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!canSubmit && (
                            <Typography variant="caption" color="error">
                                Please fill all required fields
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            sx={{
                                px: 4,
                                borderColor: '#667eea',
                                color: '#667eea',
                                '&:hover': {
                                    borderColor: '#764ba2',
                                    bgcolor: 'rgba(102, 126, 234, 0.04)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <MDButton
                            type="submit"
                            color="success"
                            disabled={!canSubmit || loading || specificationErrors.some(specError => Object.values(specError).some(e => e))}
                            sx={{
                                px: 5,
                                py: 1.2,
                                background: loading || !canSubmit ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: loading || !canSubmit ? undefined : '0 4px 15px rgba(102, 126, 234, 0.4)',
                                '&:hover': {
                                    boxShadow: loading || !canSubmit ? undefined : '0 6px 20px rgba(102, 126, 234, 0.6)'
                                }
                            }}
                        >
                            {loading ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={20} color="inherit" />
                                    <span>Updating...</span>
                                </Stack>
                            ) : (
                                "Update Product"
                            )}
                        </MDButton>
                    </Stack>
                </DialogActions>
            </MDBox>
        </Dialog>
    );
};

export default EditProductModal;
